import { logDebug } from '@/utils/secureLogger'

/**
 * Check if a session is within 12 hours of its start time
 * @param startAt - Session start time (Firestore timestamp or Date)
 * @returns Object with isWithin12Hours boolean and hoursRemaining number
 */
export function isSessionWithin12Hours(startAt: any): { isWithin12Hours: boolean; hoursRemaining: number } {
  try {
    let startTime: Date
    
    // Handle different timestamp formats
    if (startAt && typeof startAt.toDate === 'function') {
      // Firestore timestamp
      startTime = startAt.toDate()
    } else if (startAt instanceof Date) {
      // Already a Date object
      startTime = startAt
    } else if (typeof startAt === 'string') {
      // ISO string
      startTime = new Date(startAt)
    } else {
      logDebug('Invalid startAt format', { startAt })
      return { isWithin12Hours: false, hoursRemaining: 0 }
    }
    
    const now = new Date()
    const timeDiff = startTime.getTime() - now.getTime()
    const hoursRemaining = Math.ceil(timeDiff / (1000 * 60 * 60))
    
    const isWithin12Hours = hoursRemaining <= 12 && hoursRemaining > 0
    
    logDebug('Session time check', { 
      startTime: startTime.toISOString(), 
      now: now.toISOString(), 
      hoursRemaining, 
      isWithin12Hours 
    })
    
    return { isWithin12Hours, hoursRemaining }
  } catch (error) {
    logDebug('Error checking session time', { error, startAt })
    return { isWithin12Hours: false, hoursRemaining: 0 }
  }
}

/**
 * Get a user-friendly message for session time restrictions
 * @param hoursRemaining - Number of hours remaining until session
 * @returns Formatted message string
 */
export function getSessionTimeMessage(hoursRemaining: number): string {
  if (hoursRemaining <= 0) {
    return "This session has already started or passed."
  } else if (hoursRemaining === 1) {
    return "This session starts in 1 hour. You cannot reschedule or cancel anymore."
  } else if (hoursRemaining < 12) {
    return `This session starts in ${hoursRemaining} hours. You cannot reschedule or cancel anymore.`
  } else {
    return `This session starts in ${hoursRemaining} hours.`
  }
}

/**
 * Check if a session can be cancelled based on time restrictions
 * @param startAt - Session start time (Date object)
 * @param status - Session status
 * @returns Object with canCancel boolean and timeRestrictionMessage string
 */
export function canCancelSession(startAt: Date, status: string): {
  canCancel: boolean;
  timeRestrictionMessage: string;
} {
  const now = new Date();
  const minutesSinceStart = (now.getTime() - startAt.getTime()) / (1000 * 60);
  const hoursUntilSession = (startAt.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  // Special handling for in-progress sessions
  if (status === 'in-progress') {
    // Must wait 15 minutes after session start before cancelling
    if (minutesSinceStart < 15) {
      const minutesRemaining = Math.ceil(15 - minutesSinceStart);
      return {
        canCancel: false,
        timeRestrictionMessage: `Please wait ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} before cancelling this session`
      };
    }
    return {
      canCancel: true,
      timeRestrictionMessage: ''
    };
  }
  
  // For sessions that haven't started yet
  if (hoursUntilSession < 0) {
    return {
      canCancel: false,
      timeRestrictionMessage: 'Cannot cancel a session that has already started'
    };
  }
  
  // If session is within 12 hours, cannot cancel
  if (hoursUntilSession <= 12) {
    return {
      canCancel: false,
      timeRestrictionMessage: `Cannot cancel a session that starts in ${Math.ceil(hoursUntilSession)} hours`
    };
  }
  
  return {
    canCancel: true,
    timeRestrictionMessage: ''
  };
}

/**
 * Check if a session can be rescheduled based on time restrictions
 * @param startAt - Session start time (Date object)
 * @param status - Session status
 * @returns Object with canReschedule boolean and timeRestrictionMessage string
 */
export function canRescheduleSession(startAt: Date, status: string): {
  canReschedule: boolean;
  timeRestrictionMessage: string;
} {
  const now = new Date();
  const hoursUntilSession = (startAt.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  // Cannot reschedule in-progress sessions
  if (status === 'in-progress') {
    return {
      canReschedule: false,
      timeRestrictionMessage: 'Cannot reschedule a session that is in progress'
    };
  }
  
  // If session has already started, cannot reschedule
  if (hoursUntilSession < 0) {
    return {
      canReschedule: false,
      timeRestrictionMessage: 'Cannot reschedule a session that has already started'
    };
  }
  
  // If session is within 12 hours, cannot reschedule
  if (hoursUntilSession <= 12) {
    return {
      canReschedule: false,
      timeRestrictionMessage: `Cannot reschedule a session that starts in ${Math.ceil(hoursUntilSession)} hours`
    };
  }
  
  return {
    canReschedule: true,
    timeRestrictionMessage: ''
  };
}

/**
 * Check if a session can be marked as complete (for tutors)
 * @param startAt - Session start time (Date object)
 * @param status - Session status
 * @returns Object with canComplete boolean and timeRestrictionMessage string
 */
export function canCompleteSession(startAt: Date, status: string): {
  canComplete: boolean;
  timeRestrictionMessage: string;
} {
  // Only in-progress sessions can be marked complete
  if (status !== 'in-progress') {
    return {
      canComplete: false,
      timeRestrictionMessage: 'Only in-progress sessions can be marked as complete'
    };
  }
  
  const now = new Date();
  const minutesSinceStart = (now.getTime() - startAt.getTime()) / (1000 * 60);
  
  // Must wait 15 minutes after session start before marking complete
  if (minutesSinceStart < 15) {
    const minutesRemaining = Math.ceil(15 - minutesSinceStart);
    return {
      canComplete: false,
      timeRestrictionMessage: `Please wait ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} before marking this session as complete`
    };
  }
  
  return {
    canComplete: true,
    timeRestrictionMessage: ''
  };
}

/**
 * Check if a session should transition to in-progress status
 * @param startTime - Session start time (Date object)
 * @param currentStatus - Current session status
 * @returns boolean indicating if session should be in-progress
 */
export function shouldTransitionToInProgress(startTime: Date, currentStatus: string): boolean {
  if (currentStatus !== 'confirmed') return false
  
  const now = new Date()
  return now >= startTime
}

/**
 * Check if a session should transition to completed status
 * @param endTime - Session end time (Date object)
 * @param currentStatus - Current session status
 * @returns boolean indicating if session should be completed
 */
export function shouldTransitionToCompleted(endTime: Date, currentStatus: string): boolean {
  if (currentStatus !== 'in-progress') return false
  
  const now = new Date()
  return now >= endTime
}

/**
 * Get the appropriate session status based on current time
 * @param startTime - Session start time (Date object)
 * @param endTime - Session end time (Date object)
 * @param currentStatus - Current session status
 * @returns The status the session should have
 */
export function getAutoSessionStatus(startTime: Date, endTime: Date, currentStatus: string): string {
  const now = new Date()
  
  // If session has ended, it should be completed
  if (now >= endTime && currentStatus === 'in-progress') {
    return 'completed'
  }
  
  // If session has started, it should be in-progress
  if (now >= startTime && currentStatus === 'confirmed') {
    return 'in-progress'
  }
  
  // Otherwise, keep current status
  return currentStatus
}